<template>
  <ContentWrap
    id="milestonesPageSkeleton"
    :withTabs="false"
    size="40-60"
    class="milestones milestone-page-skeleton"
    idLeft="milestonesSkeletonLeft"
    idRight="milestonesSkeletonRight"
  >
    <template #left>
      <div class="milestone-page-skeleton__goal-card">
        <div class="milestone-page-skeleton__goal-header">
          <Skeletor class="milestone-page-skeleton__goal-title" />
          <Skeletor class="milestone-page-skeleton__goal-menu" />
        </div>

        <div class="milestone-page-skeleton__goal-copy">
          <Skeletor class="milestone-page-skeleton__line milestone-page-skeleton__line--wide" />
          <Skeletor class="milestone-page-skeleton__line milestone-page-skeleton__line--medium" />
        </div>

        <div
          v-for="section in 3"
          :key="section"
          class="milestone-page-skeleton__goal-section"
        >
          <Skeletor class="milestone-page-skeleton__section-title" />
          <Skeletor class="milestone-page-skeleton__line milestone-page-skeleton__line--wide" />
        </div>

        <div class="milestone-page-skeleton__goal-stats">
          <div class="milestone-page-skeleton__stat-card">
            <div class="milestone-page-skeleton__stat-info">
              <Skeletor class="milestone-page-skeleton__stat-label" />
              <Skeletor class="milestone-page-skeleton__stat-value" />
            </div>
            <Skeletor class="milestone-page-skeleton__stat-circle" />
          </div>
          <Skeletor class="milestone-page-skeleton__video" />
        </div>

        <div class="milestone-page-skeleton__features">
          <Skeletor
            v-for="feature in 4"
            :key="feature"
            class="milestone-page-skeleton__feature"
          />
        </div>

        <Skeletor class="milestone-page-skeleton__roadmap-link" />
      </div>
    </template>

    <template #right>
      <div class="milestone-page-skeleton__milestone">
        <div class="milestone-page-skeleton__milestone-header">
          <div class="milestone-page-skeleton__milestone-title-wrap">
            <Skeletor class="milestone-page-skeleton__dot" />
            <Skeletor class="milestone-page-skeleton__milestone-title" />
            <Skeletor class="milestone-page-skeleton__milestone-date" />
          </div>
          <div class="milestone-page-skeleton__actions">
            <Skeletor class="milestone-page-skeleton__icon" />
            <Skeletor class="milestone-page-skeleton__icon" />
            <Skeletor class="milestone-page-skeleton__nav" />
            <Skeletor class="milestone-page-skeleton__nav" />
          </div>
        </div>

        <div class="milestone-page-skeleton__milestone-content">
          <div class="milestone-page-skeleton__metrics">
            <div class="milestone-page-skeleton__metric-list">
              <Skeletor
                v-for="metric in 3"
                :key="metric"
                class="milestone-page-skeleton__metric"
              />
            </div>
            <Skeletor class="milestone-page-skeleton__progress" />
          </div>

          <div class="milestone-page-skeleton__epics">
            <Skeletor
              v-for="line in 12"
              :key="line"
              class="milestone-page-skeleton__epic-line"
            />
          </div>
        </div>
      </div>
    </template>
  </ContentWrap>
</template>

<script setup lang="ts">
import { Skeletor } from "vue-skeletor"

import ContentWrap from "@/components/ContentWrap/index.vue"
</script>

<style scoped lang="scss">
.milestone-page-skeleton {
  --milestone-skeleton-radius: 8px;

  &__goal-card {
    display: flex;
    height: 100%;
    flex-direction: column;
    padding: var(--step6);
    gap: var(--step4);

    @media (max-width: 1536px) {
      padding: var(--step4);
    }
  }

  &__goal-header,
  &__milestone-header,
  &__milestone-title-wrap,
  &__actions,
  &__goal-stats,
  &__metric-list {
    display: flex;
    align-items: center;
  }

  &__goal-header,
  &__milestone-header,
  &__metrics {
    justify-content: space-between;
  }

  &__goal-title {
    width: 28%;
    height: 22px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__goal-menu {
    width: var(--step1);
    height: var(--step6);
    border-radius: var(--milestone-skeleton-radius);
  }

  &__goal-copy,
  &__goal-section,
  &__features {
    display: flex;
    flex-direction: column;
    gap: var(--step2);
  }

  &__section-title {
    width: 36%;
    height: 18px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__line {
    height: 14px;
    border-radius: var(--milestone-skeleton-radius);

    &--wide {
      width: 88%;
    }

    &--medium {
      width: 62%;
    }
  }

  &__goal-stats {
    gap: var(--step4);
  }

  &__stat-card {
    display: flex;
    min-height: 112px;
    flex: 1 1 0;
    align-items: center;
    justify-content: space-between;
    padding: var(--step6);
    border: 1px solid var(--monochrome-02);
    border-radius: var(--milestone-skeleton-radius);
    gap: var(--step6);

    @media (max-width: 1536px) {
      min-height: 86px;
      padding: var(--step4);
    }
  }

  &__stat-info {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: var(--step1);
  }

  &__stat-label {
    width: 78%;
    height: 14px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__stat-value {
    width: 72px;
    height: 22px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__stat-circle {
    width: 58px;
    height: 58px;
    flex: 0 0 58px;
    border-radius: 50%;

    @media (max-width: 1536px) {
      width: 48px;
      height: 48px;
      flex-basis: 48px;
    }
  }

  &__video {
    height: 112px;
    flex: 0 0 34%;
    border-radius: var(--milestone-skeleton-radius);

    @media (max-width: 1536px) {
      height: 86px;
    }
  }

  &__feature {
    width: 100%;
    height: 32px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__roadmap-link {
    width: 24%;
    height: 18px;
    border-radius: var(--milestone-skeleton-radius);
    margin-top: auto;
  }

  &__milestone {
    display: flex;
    height: 100%;
    flex-direction: column;
    gap: var(--step5);
  }

  &__milestone-title-wrap {
    min-width: 0;
    gap: var(--step3);
  }

  &__dot {
    width: var(--step2);
    height: var(--step2);
    flex: 0 0 var(--step2);
    border-radius: 50%;
  }

  &__milestone-title {
    width: 132px;
    height: 20px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__milestone-date {
    width: 178px;
    height: 18px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__actions {
    gap: var(--step3);
  }

  &__icon {
    width: var(--step5);
    height: var(--step5);
    border-radius: var(--milestone-skeleton-radius);
  }

  &__nav {
    width: var(--step8);
    height: var(--step8);
    border-radius: 50%;
  }

  &__milestone-content {
    display: flex;
    overflow: hidden;
    height: 100%;
    flex-direction: column;
    padding: var(--step6);
    border-radius: var(--milestone-skeleton-radius);
    background: var(--monochrome-01);
    gap: var(--step4);

    @media (max-width: 1536px) {
      padding: var(--step4);
    }
  }

  &__metrics {
    display: flex;
    gap: var(--step6);
  }

  &__metric-list {
    gap: var(--step6);
  }

  &__metric {
    width: 96px;
    height: 16px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__progress {
    width: 170px;
    height: 16px;
    border-radius: var(--milestone-skeleton-radius);
  }

  &__epics {
    display: flex;
    overflow: hidden;
    flex-direction: column;
    gap: var(--step6);
  }

  &__epic-line {
    width: 100%;
    height: 24px;
    flex-shrink: 0;
    border-radius: var(--radius-item);
  }
}

.dark {
  .milestone-page-skeleton {
    &__milestone-content {
      background: var(--monochrome-20);
    }

    &__stat-card {
      border-color: var(--monochrome-22);
    }
  }
}
</style>
